import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

// Reusable professional record form dialog.
// fields: [{ key, label, type: text|number|textarea|select|checkbox|date, options, required, placeholder, span2, showIf }]
export function RecordDialog({ open, onOpenChange, title, description, fields, initial, onSubmit, testid, submitLabel = "Simpan" }) {
  const [f, setF] = useState(initial || {});
  const [saving, setSaving] = useState(false);
  // B-462: start from `initial` when the dialog opens. Callers pass `initial={{}}` inline, so following its identity
  // wiped whatever was typed on every parent re-render.
  const wasOpen = useRef(false);
  useEffect(() => { if (open && !wasOpen.current) setF(initial || {}); wasOpen.current = open; }, [open, initial]);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const visible = fields.filter((fl) => !fl.showIf || fl.showIf(f));

  const submit = async () => {
    if (saving) return;
    for (const fl of visible) {
      if (fl.required && fl.type !== "checkbox" && !String(f[fl.key] ?? "").trim()) {
        return toast.error(`${fl.label} wajib diisi`);
      }
    }
    setSaving(true);   // one request at a time: a second click must not create a second record
    try { await onSubmit(f); } finally { setSaving(false); }
  };

  const renderField = (fl) => {
    const v = f[fl.key];
    switch (fl.type) {
      case "textarea":
        return <Textarea rows={fl.rows || 2} className="text-xs" value={v || ""} placeholder={fl.placeholder} onChange={(e) => set(fl.key, e.target.value)} data-testid={`fld-${fl.key}`} />;
      case "number":
        return <Input type="number" className="h-8 text-xs" value={v ?? ""} placeholder={fl.placeholder} onChange={(e) => set(fl.key, e.target.value === "" ? "" : Number(e.target.value))} data-testid={`fld-${fl.key}`} />;
      case "date":
        return <Input type="date" className="h-8 text-xs" value={v || ""} onChange={(e) => set(fl.key, e.target.value)} data-testid={`fld-${fl.key}`} />;
      case "select":
        return (
          <Select value={v || ""} onValueChange={(val) => set(fl.key, val)}>
            <SelectTrigger className="h-8 text-xs" data-testid={`fld-${fl.key}`}><SelectValue placeholder={fl.placeholder || "Pilih"} /></SelectTrigger>
            <SelectContent>{(fl.options || []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
          </Select>
        );
      case "checkbox":
        return <label className="flex items-center gap-2 text-sm pt-1"><Checkbox checked={!!v} onCheckedChange={(val) => set(fl.key, !!val)} data-testid={`fld-${fl.key}`} />{fl.checkboxLabel || fl.label}</label>;
      default:
        return <Input className="h-8 text-xs" value={v || ""} placeholder={fl.placeholder} onChange={(e) => set(fl.key, e.target.value)} data-testid={`fld-${fl.key}`} />;
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid={testid}>
        <DialogHeader><DialogTitle>{title}</DialogTitle>
          <DialogDescription className={description ? "" : "sr-only"}>{description || "Formulir input"}</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3 max-h-[70vh] overflow-y-auto pr-1">
          {visible.map((fl) => (
            <div key={fl.key} className={fl.type === "textarea" || fl.span2 || fl.type === "checkbox" ? "col-span-2" : ""}>
              {fl.type !== "checkbox" && <Label className="text-xs">{fl.label}{fl.required && <span className="text-red-500"> *</span>}</Label>}
              {renderField(fl)}
            </div>
          ))}
        </div>
        <DialogFooter><Button size="sm" onClick={submit} disabled={saving} data-testid={`${testid}-submit`}>{saving ? "Menyimpan…" : submitLabel}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
